<template>
  <v-app :style="appStyle" class="default-layout-container">
    <transition name="fade">
      <Alerts v-if="alertArray.length" :alertArray="alertArray" @dismiss-alert="handleDismissAlert" />
    </transition>
    
    <v-app-bar app class="modern-header" height="80px" flat>
      <div class="header-content">
        <div class="page-title d-none d-md-flex">
          <v-icon class="mr-2">{{ pageIcon }}</v-icon>
          {{ pageTitle }}
        </div>
        <div class="company-info">
          <div class="company-logo" :style="{ backgroundColor: logoBackgroundColor }">
            <img 
              :src="clientLogo" 
              v-if="clientLogo"
              alt="Company Logo"
              class="company-logo-img" />
            <div v-else class="company-placeholder">Company Logo</div>
          </div>
          <v-select
            v-if="clients.length > 1"
            :items="clients"
            :item-text="item => item.name"
            :item-value="item => item.id"
            :value="clients.find(c => c.name === selectedClient)?.id"
            @input="selectClient"
            label="Practice"
            dense
            hide-details
            class="client-selector" />
          <div v-else class="company-name">{{ selectedClient }}</div>
        </div>
      </div>
    </v-app-bar>

    <v-main class="modern-main">
      <ServiceMessage class="px-5" />
      
      <DesktopNavigation v-if="this.$vuetify.breakpoint.mdAndUp" />
      
      <div class="main-content">
        <transition name="page-transition" mode="out-in">
          <keep-alive include="users">
            <router-view :key="$route.fullPath" />
          </keep-alive>
        </transition>
      </div>
    </v-main>

    <MobileNavigation v-if="this.$vuetify.breakpoint.smAndDown" />

    <footer class="page-footer">
      <div class="footer-left">COPYRIGHT ACREW ©2025</div>
      <div class="footer-links">
        <a href="https://acrewdental.com/privacy-policy/" class="footer-link">PRIVACY POLICY</a>
        <span class="divider">|</span>
        <a href="https://acrewdental.com/terms-of-service/" class="footer-link">TERMS & CONDITIONS</a>
      </div>
    </footer>
  </v-app>
</template>

<script>
import DataService from '../../_services/DataService';
import Alerts from '../components/Alerts.vue';
import MobileNavigation from '../components/Navigation/MobileNavigation.vue';
import DesktopNavigation from '../components/Navigation/DesktopNavigation.vue';
import ServiceMessage from '../components/ServiceMessage.vue';
import { ALERT_TIMEOUT_MS, loadClientPreferences } from '../utils/UxUtils.js';

export default {
  components: {
    Alerts,
    MobileNavigation,
    DesktopNavigation,
    ServiceMessage,
  },
  data() {
    return {
      clientLogo: '',
      clientColor: '',
      useWhiteBackground: false,
      currentTime: new Date().toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
      clients: [],
      selectedClient: '',
      selectedLocale: 'EN',
      loading2: false,
      items2: [],
      search: null,
      select: null,
      states: [],
      alertArray: [],
      tabNotificationTimer: null,
    };
  },
  computed: {
    appStyle() {
      return {
        '--brand-color': this.clientColor || 'white',
        overflowX: (this.$vuetify.breakpoint.smAndDown ||
          this.$vuetify.breakpoint.mdAndUp ) ?
          'hidden' :
          'auto',
        overflowY: ( this.$vuetify.breakpoint.smAndDown ||
          this.$vuetify.breakpoint.mdAndUp )
          ? 'hidden'
          : 'auto',
      };
    },
    logoBackgroundColor() {
      return this.whiteBehindLogo ? 'white' : (this.clientColor || 'white');
    },
    pageTitle() {
      if (this.$route.path.includes('/email_template_editor')) {
        return 'Template Editor';
      }
      if (this.$route.path.includes('/account')) {
        return 'Account Settings';
      }
      if (this.$route.path.includes('/patient_schedule') || this.$route.path.includes('/scheduled_appointments')) {
        return 'Schedule';
      }
      if (this.$route.path.includes('/treatment_plan')) {
        return 'Treatment Plan';
      }
      if (this.$route.path.includes('/bill_pay')) {
        return 'Bill Pay';
      }
      if (this.$route.path.includes('/reporting')) {
        return 'Reporting';
      }
      if (this.$route.path.includes('/admin')) {
        return 'Admin';
      }
      if (this.$route.path.includes('/messaging')) {
        return 'Messaging';
      }
      return 'Dashboard';
    },
    pageIcon() {
      if (this.$route.path.includes('/email_template_editor')) {
        return 'mdi-file-document-edit';
      }
      if (this.$route.path.includes('/account')) {
        return 'mdi-account-cog';
      }
      if (this.$route.path.includes('/patient_schedule') || this.$route.path.includes('/scheduled_appointments')) {
        return 'mdi-calendar-blank';
      }
      if (this.$route.path.includes('/treatment_plan')) {
        return 'mdi-clipboard-text-outline';
      }
      if (this.$route.path.includes('/bill_pay')) {
        return 'mdi-credit-card';
      }
      if (this.$route.path.includes('/reporting')) {
        return 'mdi-chart-line';
      }
      if (this.$route.path.includes('/admin')) {
        return 'mdi-shield-account';
      }
      if (this.$route.path.includes('/messaging')) {
        return 'mdi-message-text';
      }
      return 'mdi-view-dashboard';
    },
  },
  methods: {
    async getUserClients() {
      const response = await DataService.getUserClients();
      if (response.status === 200) {
        this.clients = response.data;

        this.selectedClient = this.clients.find((client) => client.id === DataService.getClientId()).name;
      }
    },
    async selectClient(clientId) {
      try {
        await DataService.changeClientSelection(clientId);
        window.location.reload();
      } catch (ex) {
        this.$root.$emit('raise_alert', {
          color: 'info',
          text: ex.message,
        });
      }
    },
    raiseAlert(alert) {
      // Add unique ID for tracking
      const alertWithId = {
        ...alert,
        id: Date.now() + Math.random(),
      };
      
      // Add to existing alerts instead of clearing
      this.alertArray.push(alertWithId);
      
      // Auto-dismiss after timeout
      setTimeout(() => {
        const index = this.alertArray.findIndex(a => a.id === alertWithId.id);
        if (index > -1) {
          this.alertArray.splice(index, 1);
        }
      }, ALERT_TIMEOUT_MS);
    },
    dismissAlert() {
      this.alert = false;
    },
    handleDismissAlert(index) {
      // Add fade-out class before removing
      if (this.alertArray[index]) {
        this.alertArray.splice(index, 1);
      }
    },
    async tabNotificationUpdate() {
      const messageCountResponse = await DataService.getUnreadMessageCount();
      document.title = document.title.replace(/ *\([^)]*\) */g, '');
      if (messageCountResponse.status === 200 && messageCountResponse.data > 0) {
        document.title += ` (${messageCountResponse.data})`;
      } else if (messageCountResponse.status === 401 || messageCountResponse.status === 403) {
        this.clearTabNotificationTimer();
      }
    },
    clearTabNotificationTimer() {
      if (this.tabNotificationTimer) {
        clearInterval(this.tabNotificationTimer);
        document.title = 'Acrew';
      }
    },
    onLogout() {
      this.clearTabNotificationTimer();
    },
  },
  async mounted() {
    document.title = 'Acrew';

    await Promise.all([
      loadClientPreferences(this),
      this.getUserClients(),
    ]);

    if (DataService.getRole() && DataService.getRole() !== 'patient') {
      this.tabNotificationUpdate();
      this.tabNotificationTimer = setInterval(this.tabNotificationUpdate, 60000);
    }
    this.$root.$on('raise_alert', this.raiseAlert);
    this.$root.$on('dismiss_alert', this.dismissAlert);
    this.$root.$on('logout', this.onLogout);
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';
@import '@/scss/fonts.scss';
@import '@/scss/global.scss';
@import '@/scss/buttons.scss';
@import '@/scss/modern-form.scss';
@import '@/scss/modern-modal.scss';

// Component-specific colors
$header-bg: white;
$header-shadow: rgba(0, 0, 0, 0.1);
$header-border: #e5e7eb;
$page-title-color: #2d3748;
$company-name-color: #2d3748;
$dropdown-icon-color: #718096;
$footer-text-color: #6c7b87;
$footer-link-color: #11405c;
$footer-border: #d1d5db;
$main-bg: #e5f0f5;

.default-layout-container {
  .modern-header {
    background-color: $header-bg !important;
    box-shadow: 0 2px 8px $header-shadow;
    border-bottom: 1px solid $header-border;
    z-index: 100;
      
    @media (max-width: $breakpoint-mobile) {
      background-color: $header-bg !important;
      box-shadow: 0 2px 8px $header-shadow;
      border-bottom: 1px solid $header-border;
    }
      
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: 0 24px;
        
      @media (max-width: $breakpoint-tablet) {
        padding: 0 16px;
        justify-content: center;
      }
    }
      
    .page-title {
      display: flex;
      align-items: center;
      font-size: 24px;
      font-weight: 600;
      color: $page-title-color;
        
      @media (max-width: $breakpoint-mobile) {
        .v-icon {
          display: none;
        }
      }
        
      @media (max-width: $breakpoint-tablet) {
        font-size: 20px;
      }
    }
      
    .company-info {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 12px 20px;
      background-color: $header-bg;
      border-radius: 8px;
      min-width: 200px;
        
      @media (max-width: $breakpoint-tablet) {
        min-width: auto;
        padding: 8px 20px;
        gap: 12px;
      }

      .client-selector {
        min-width: 180px;
        max-width: 250px;
        cursor: pointer;

        ::v-deep .v-input__slot {
          cursor: pointer;
        }

        ::v-deep input {
          cursor: pointer;
        }
      }
    }
      
    .company-logo {
      width: 140px;
      height: 54px;
      border-radius: 6px;
      border: 1px solid rgba(0, 0, 0, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
        
      @media (max-width: $breakpoint-tablet) {
        width: 112px;
        height: 44px;
      }
        
      &-img {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        border-radius: 6px;
        object-fit: contain;
      }
    }
      
    .company-placeholder {
      color: white;
      font-size: 10px;
      text-align: center;
    }
      
    .company-name {
      font-weight: 600;
      color: $company-name-color;
        
      @media (max-width: $breakpoint-tablet) {
        font-size: 14px;
      }
    }
      
    .dropdown-icon {
      color: $dropdown-icon-color;
    }
  }

  .modern-main {
    padding-top: $header-height !important;

    .main-content {
      margin-left: 0;
      padding: 16px 16px 0 16px;
      min-height: calc(100vh - #{$header-height + $footer-height});
      background-color: $main-bg !important;
    }

    @media (max-width: $breakpoint-tablet) {
      .main-content {
        margin-left: 0;
        padding: 12px;
      }
    }
  }

  .page-footer {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 32px;
    color: $footer-text-color;
    font-size: 12px;
    letter-spacing: 0.3px;
    border-top: 1px solid $footer-border;
    padding: 20px 32px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
      
    @media (max-width: $breakpoint-mobile) {
      display: none;
    }
      
    .footer-links {
      display: flex;
      align-items: center;
      gap: 10px;
    }
      
    .footer-link {
      color: $footer-link-color;
      text-decoration: none;
      font-weight: 700;
        
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.v-overlay.v-overlay--active.theme--dark {
  z-index: 9999 !important;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.page-transition-enter-active {
  transition: all 0.3s ease-out;
}

.page-transition-leave-active {
  transition: all 0.2s ease-in;
}

.page-transition-enter {
  opacity: 0;
  transform: translateY(10px);
}

.page-transition-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
<style lang="scss">
@import '@/scss/variables.scss';

.default-layout-container {
  @media (min-width: $content-max-width) {
    .v-main__wrap {
      max-width: $content-max-width;

      .container {
        max-width: none !important;
      }
    }

    .v-toolbar__content {
      max-width: $content-max-width;
    }
  }
}
</style>
